import { Actions } from 'vuex-smart-module';
import { ResponseStatuses } from '@/enums';
import { Filters, FiltersPagination } from '@/types';
import { AxiosResponse } from 'axios';
import { Repairs } from '@/modules/repairs/types';
import { RepairsService } from '../../../services';
import RepairsInfoGetters from './getters';
import RepairsInfoMutations from './mutations';
import RepairsInfoState from './state';

const repairsService = new RepairsService();

export default class RepairsInfoActions extends Actions<
  RepairsInfoState,
  RepairsInfoGetters,
  RepairsInfoMutations,
  RepairsInfoActions
> {
  // получение данных о компаниях-пользователях ЛКП с сервера

  async get_repairs() {
    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.table_options;
      const payload: FiltersPagination<Filters> = {
        filters: this.state.filters,
        pagination: {
          limit: itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      this.mutations.TOGGLE_LOADING_REPAIRS_INFO(true);
      const { data, meta } = await repairsService.get_repairs(payload);

      this.mutations.SET_REPAIRS_LIST(data.items);
      this.mutations.SET_CLIENT_COMPANIES(data.name_client_companies);
      this.mutations.SET_OPERATION_TYPES(data.operation_types);
      this.mutations.SET_REPAIR_TYPES(data.repair_types);
      this.mutations.SET_REPAIR_STATUSES(data.side_statuses);
      this.mutations.UPD_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'limit', value: meta.limit });
    } catch (error) {
      console.log(error);
    } finally {
      this.mutations.TOGGLE_LOADING_REPAIRS_INFO(false);
    }
  }

  async get_repair_by_id(id: string | number) {
    this.mutations.TOGGLE_LOADING_REPAIRS_INFO(true);

    try {
      const { data, code } = await repairsService.get_repair_by_id(id);

      this.mutations.SET_REPAIR_DETAILS(data);
      return code;
    } catch (error) {
      console.log(error);
      return (error as AxiosResponse).status;
    } finally {
      this.mutations.TOGGLE_LOADING_REPAIRS_INFO(false);
    }
  }

  async get_repair_components_set(id: string | number) {
    try {
      const { data, code = ResponseStatuses.Fail.FORBIDDEN } = await repairsService.get_repair_components_set(id);

      this.mutations.SET_REPAIR_COMPONENTS_SET(data);
      return code;
    } catch (error) {
      console.log(error);
      return (error as AxiosResponse).status;
    }
  }

  async add_new_component(params: { id: string | number; payload: Repairs.ComponentPayload }) {
    const { id, payload } = params;

    try {
      const data = await repairsService.add_new_component(id, payload);

      return data.code;
    } catch (error) {
      console.log(error);
    }
  }

  async delete_component(id: string | number) {
    try {
      const { code = ResponseStatuses.Success.OK } = await repairsService.delete_component(id);

      return code;
    } catch (error) {
      console.log(error);
    }
  }

  async get_repair_diagnostics(id: string | number) {
    try {
      const { data, code = ResponseStatuses.Fail.FORBIDDEN } = await repairsService.get_repair_diagnostics(id);

      const {
        diagnostic,
        symptom_types,
        repair_types,
        operation_types,
        equipment_categories,
        condition_code_types,
      } = data;

      this.mutations.SET_REPAIR_DIAGNOSTICS(diagnostic);
      this.mutations.SET_REPAIR_DIAGNOSTICS_SELECTS({
        symptom_types,
        repair_types,
        operation_types,
        equipment_categories,
        condition_code_types,
      });

      return code;
    } catch (error) {
      console.log(error);
      return (error as AxiosResponse).status;
    }
  }

  async update_diagnostics(id: string | number) {
    try {
      const { editable_diagnostics } = this.state;

      const { success } = await repairsService.upd_diagnostics(id, editable_diagnostics);

      await this.dispatch('get_repair_diagnostics', id);
      return success;
    } catch (error) {
      console.log(error);
    }
  }

  async get_repair_stages(id: string | number) {
    try {
      const { data, code = ResponseStatuses.Fail.FORBIDDEN } = await repairsService.get_repair_stages(id);

      this.mutations.SET_REPAIR_STAGES(data);
      return code;
    } catch (error) {
      console.log(error);
      return (error as AxiosResponse).status;
    }
  }

  async delete_repair_stage(id: string | number) {
    try {
      const { code = ResponseStatuses.Success.OK } = await repairsService.delete_repair_stage(id);

      return code;
    } catch (error) {
      console.log(error);
    }
  }

  async add_repair_stage(params: { id: string | number; payload: Repairs.StagePayload; comment?: string }) {
    const { id, payload, comment } = params;

    if (comment) {
      payload.comment = comment;
    }

    try {
      const data = await repairsService.add_repair_stage(id, payload);

      return data.code;
    } catch (error) {
      console.log(error);
    }
  }

  async upd_repair_stage(params: { id: string | number; payload: Pick<Repairs.Stage, 'comment'> }) {
    const { id, payload } = params;

    try {
      const data = await repairsService.upd_repair_stage(id, payload);

      return data.code;
    } catch (error) {
      console.log(error);
    }
  }

  async get_repair_products(id: string | number) {
    try {
      const { data, code = ResponseStatuses.Fail.FORBIDDEN } = await repairsService.get_repair_products(id);

      this.mutations.SET_REPAIR_PRODUCTS(data);
      return code;
    } catch (error) {
      console.log(error);
      return (error as AxiosResponse).status;
    }
  }

  // async get_possible_repair_products(id: string | number) {
  //   try {
  //     const { data, code = ResponseStatuses.Fail.FORBIDDEN } = await repairsService.get_possible_repair_products(id);

  //     this.mutations.SET_POSSIBLE_REPAIR_PRODUCTS(data);
  //     return code;
  //   } catch (error) {
  //     console.log(error);
  //     return (error as AxiosResponse).status;
  //   }
  // }

  async add_repair_product(params: { id: string | number; payload: Repairs.AddProductPayload }) {
    const { id, payload } = params;

    try {
      const data = await repairsService.add_repair_product(id, payload);

      return data.code;
    } catch (error) {
      console.log(error);
    }
  }

  async delete_repair_product(id: string | number) {
    try {
      const { code = ResponseStatuses.Success.OK } = await repairsService.delete_repair_product(id);

      return code;
    } catch (error) {
      console.log(error);
    }
  }

  // #TODO: remove
  async search_repair_products(val: string) {
    try {
      const { data } = await repairsService.search_repair_products(val);

      this.mutations.SET_REPAIR_PRODUCTS_FOUND(data);
    } catch (error) {
      console.log(error);
    }
  }

  // #TODO: change
  // async upd_repair_product(params: { id: string | number;
  // payload: Pick<Repairs.Product, 'quantity'| 'serial_number'| 'price'> }) {
  async upd_repair_product(p: { r_id: string | number, id: string | number; payload: Repairs.UpdateProductPayload}) {
    const { id, payload, r_id } = p;

    try {
      const { data, code } = await repairsService.upd_repair_products(id, payload);

      this.mutations.SET_REPAIR_PRODUCTS(data.items);
      this.dispatch('get_repair_by_id', r_id);
      return code;
    } catch (error) {
      console.log(error);
    }
  }

  // async delete_installed_component(id: string | number) {
  //   try {
  //     const { code = ResponseStatuses.Success.OK } = await repairsService.delete_repair_stage(id);

  //     return code;
  //   } catch (error) {
  //     console.log(error);
  //   }
  // }

  async get_repair_documents(id: string | number) {
    try {
      const { data, code = ResponseStatuses.Fail.FORBIDDEN } = await repairsService.get_repair_documents(id);

      this.mutations.SET_REPAIR_DOCUMENTS(data);
      return code;
    } catch (error) {
      console.log(error);
      return (error as AxiosResponse).status;
    }
  }

  async delete_repair_document(id: string | number) {
    try {
      const { code = ResponseStatuses.Success.OK } = await repairsService.delete_repair_document(
        id,
      );

      return code;
    } catch (error) {
      console.log(error);
    }
  }

  async send_repair_document(params: { id: string | number; file: File }) {
    const { id, file } = params;

    try {
      const { code = ResponseStatuses.Success.OK } = await repairsService.send_repair_document(
        id,
        file,
      );

      return code;
    } catch (error) {
      console.log(error);
      return (error as AxiosResponse).status;
    }
  }

  async get_catalog_products(filtersEmpty: boolean) {
    this.mutations.SET_CATALOG_PRODUCTS_LOADING(true);

    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.catalog_products_table_options;

      const payload: Repairs.CatalogProductRequest = {
        filters: this.state.catalog_products_filters,
        filters_exist: !filtersEmpty,
        pagination: {
          limit: itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      const { data, meta } = await repairsService.get_catalog_products(payload);

      this.mutations.SET_CATALOG_PRODUCTS(data.items);

      this.mutations.UPD_CATALOG_PRODUCTS_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_CATALOG_PRODUCTS_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_CATALOG_PRODUCTS_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_CATALOG_PRODUCTS_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_CATALOG_PRODUCTS_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_CATALOG_PRODUCTS_PAGINATION_FIELD({ key: 'limit', value: meta.limit });

      return data.filters;
    } catch (error) {
      console.log(error);
    } finally {
      this.mutations.SET_CATALOG_PRODUCTS_LOADING(false);
    }
  }
}
